import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../theme';
import Header from '../../components/Header';

const ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'create', label: 'Create' },
  { key: 'edit', label: 'Edit / Update' },
  { key: 'delete', label: 'Delete' },
  { key: 'send', label: 'Send' },
  { key: 'export', label: 'Export Excel' },
];

const RoleForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [catalog, setCatalog] = useState([]);
  const [details, setDetails] = useState({ name: '', description: '', dataScope: 'all' });
  const [permissions, setPermissions] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');

  const showToast = (message) => {
    setToastmessage(message);
    setOpenToast(true);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const catRes = await fetch(`${process.env.REACT_APP_APILINK}/admin/roles/permissions`, { headers: { token } });
        const catData = await catRes.json();
        if (catData.success) setCatalog(catData.data);

        if (isEdit) {
          const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/roles/${id}`, { headers: { token } });
          const data = await res.json();
          if (data.success) {
            setDetails({
              name: data.data.name || '',
              description: data.data.description || '',
              dataScope: data.data.dataScope || 'all',
            });
            setPermissions(new Set(data.data.permissions || []));
          } else {
            showToast(data.message || 'Role not found');
          }
        }
      } catch (e) {
        console.error('RoleForm load error', e);
        showToast('Could not load role details');
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sections = useMemo(() => {
    const map = new Map();
    catalog.forEach((group) => {
      if (!map.has(group.section)) map.set(group.section, []);
      map.get(group.section).push(group);
    });
    return [...map.entries()];
  }, [catalog]);

  const toggle = (group, action) => {
    const key = `${group.key}.${action}`;
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        // Without View the other actions are unusable, so clear them too.
        if (action === 'view') group.actions.forEach((a) => next.delete(`${group.key}.${a}`));
      } else {
        next.add(key);
        // Any action needs the page itself, so tick View with it.
        if (group.actions.includes('view')) next.add(`${group.key}.view`);
      }
      return next;
    });
  };

  const sectionKeys = (groups) => groups.flatMap((g) => g.actions.map((a) => `${g.key}.${a}`));

  const toggleSection = (groups, checked) => {
    setPermissions((prev) => {
      const next = new Set(prev);
      sectionKeys(groups).forEach((k) => (checked ? next.add(k) : next.delete(k)));
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!details.name.trim()) {
      showToast('Please enter a role name');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/roles${isEdit ? `/${id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-type': 'application/json', token },
        body: JSON.stringify({ ...details, permissions: [...permissions] }),
      });
      const data = await res.json();
      if (data.message) showToast(data.message);
      if (data.success) setTimeout(() => navigate('/roles'), 1500);
    } catch (err) {
      console.error('RoleForm save error', err);
      showToast('Could not save the role');
    }
    setLoading(false);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenToast(false);
  };

  const cellSx = { borderColor: colors.grey[700], py: 0.5 };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header
          title={isEdit ? 'EDIT ROLE' : 'ADD ROLE'}
          subtitle="Tick what users with this role can see and do"
        />
        <Link to="/roles">
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Go Back
          </Button>
        </Link>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box display="grid" gap="1.25rem" pb="20px">
          <TextField
            fullWidth
            variant="filled"
            label="Role name"
            value={details.name}
            onChange={(e) => setDetails({ ...details, name: e.target.value })}
            required
          />
          <TextField
            fullWidth
            variant="filled"
            label="Description (optional)"
            value={details.description}
            onChange={(e) => setDetails({ ...details, description: e.target.value })}
          />

          <Box>
            <Typography variant="h5" fontWeight="bold" mb={0.5}>
              Destinations
            </Typography>
            <RadioGroup
              row
              value={details.dataScope}
              onChange={(e) => setDetails({ ...details, dataScope: e.target.value })}
            >
              <FormControlLabel value="all" control={<Radio color="secondary" />} label="All destinations" />
              <FormControlLabel
                value="assigned"
                control={<Radio color="secondary" />}
                label="Only destinations assigned to the user"
              />
            </RadioGroup>
          </Box>

          {sections.map(([section, groups]) => {
            const keys = sectionKeys(groups);
            const checkedCount = keys.filter((k) => permissions.has(k)).length;
            return (
              <Box key={section}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={0.5}>
                  <Typography variant="h5" fontWeight="bold">
                    {section}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="secondary"
                        checked={checkedCount === keys.length && keys.length > 0}
                        indeterminate={checkedCount > 0 && checkedCount < keys.length}
                        onChange={(e) => toggleSection(groups, e.target.checked)}
                      />
                    }
                    label="Select all"
                  />
                </Box>
                <TableContainer sx={{ backgroundColor: colors.primary[400], borderRadius: 1, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                        <TableCell sx={{ ...cellSx, minWidth: 220 }}>Module</TableCell>
                        {ACTIONS.map((a) => (
                          <TableCell key={a.key} align="center" sx={cellSx}>
                            {a.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groups.map((group) => (
                        <TableRow key={group.key}>
                          <TableCell sx={cellSx}>
                            <Typography>{group.label}</Typography>
                            {group.hint && (
                              <Typography variant="caption" color={colors.grey[300]}>
                                {group.hint}
                              </Typography>
                            )}
                          </TableCell>
                          {ACTIONS.map((a) => (
                            <TableCell key={a.key} align="center" sx={cellSx}>
                              {group.actions.includes(a.key) ? (
                                <Checkbox
                                  color="secondary"
                                  checked={permissions.has(`${group.key}.${a.key}`)}
                                  onChange={() => toggle(group, a.key)}
                                  inputProps={{ 'aria-label': `${group.label} ${a.label}` }}
                                />
                              ) : (
                                <Typography color={colors.grey[600]}>–</Typography>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}

          <Typography variant="body2" color={colors.grey[300]}>
            User management, roles and permissions, and app configuration always stay with the Super Admin.
          </Typography>

          <Button
            type="submit"
            disabled={loading}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              width: 'fit-content',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 30px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            {loading ? 'Saving...' : isEdit ? 'Update' : 'Add'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default RoleForm;
